import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export type PriceList = {
  id: string;
  name: string;
  description: string | null;
  currencyCode: string;
  isDefault: boolean;
  status: 'active' | 'archived';
};

export type EffectivePriceRow = {
  priceListId: string;
  priceListName: string;
  currencyCode: string;
  isDefault: boolean;
  /** Null is the editable SKU fallback; a value is a GP/barcode override. */
  barcode: string | null;
  unitPriceMinor: string;
  // RFL-202 price-types (null when unset).
  wholesalePriceMinor: string | null;
  minPriceMinor: string | null;
  maxPriceMinor: string | null;
  discountPriceMinor: string | null;
  costMinor: string | null;
  // RFL-344 loyalty VIP price (null when unset).
  vipPriceMinor: string | null;
  // RL-994 3-decimal fuel price in thousandths (null unless entered).
  unitPriceMilli: string | null;
};

/**
 * RFL-202 price-type keys, in display order. Retail = `unitPriceMinor`.
 * `vipPriceMinor` (RFL-344) is edited here too — it is the field re:form
 * Loyalty reads for VIP card categories, and without an editor it would ship
 * unfillable, which is the exact silent failure the ticket is about.
 *
 * `label` stays as the baked English and `labelKey` names the catalogue row, so
 * a column header resolves as `t(priceType.labelKey, priceType.label)`. Both are
 * literals here because the parity guard reads source for literal keys.
 */
export const PRICE_TYPES = [
  { key: 'unitPriceMinor', label: 'Retail', labelKey: 'pricing.vocab.retail' },
  { key: 'wholesalePriceMinor', label: 'Wholesale', labelKey: 'pricing.vocab.wholesale' },
  { key: 'minPriceMinor', label: 'Min', labelKey: 'pricing.vocab.min' },
  { key: 'maxPriceMinor', label: 'Max', labelKey: 'pricing.vocab.max' },
  { key: 'discountPriceMinor', label: 'Discount', labelKey: 'pricing.vocab.discount' },
  { key: 'vipPriceMinor', label: 'VIP', labelKey: 'pricing.vocab.vip' },
] as const;
export type PriceTypeKey = (typeof PRICE_TYPES)[number]['key'];

const LISTS_KEY = ['pricing', 'price-lists'] as const;
const EFFECTIVE_KEY = (itemId: string) => ['pricing', 'effective', itemId] as const;

export function usePriceLists(status: 'active' | 'archived' = 'active') {
  return useQuery({
    queryKey: [...LISTS_KEY, status] as const,
    queryFn: async (): Promise<PriceList[]> => {
      const res = await apiClient.get<PriceList[]>('/pricing/price-lists', {
        params: { status },
      });
      return res.data;
    },
  });
}

export function useCreatePriceList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      description?: string;
      currencyCode: string;
      isDefault: boolean;
    }): Promise<PriceList> => {
      const res = await apiClient.post<PriceList>('/pricing/price-lists', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LISTS_KEY }),
  });
}

export function useArchivePriceList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/pricing/price-lists/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LISTS_KEY }),
  });
}

/** RFL-366 — bring an archived list back; the server never restores a second default. */
export function useUnarchivePriceList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<PriceList> => {
      const res = await apiClient.post<PriceList>(`/pricing/price-lists/${id}/unarchive`);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LISTS_KEY }),
  });
}

export function useEffectivePrices(itemId: string | undefined) {
  return useQuery({
    queryKey: EFFECTIVE_KEY(itemId ?? ''),
    enabled: itemId !== undefined && itemId !== '',
    queryFn: async (): Promise<EffectivePriceRow[]> => {
      const res = await apiClient.get<EffectivePriceRow[]>(
        `/pricing/items/${itemId}/effective-prices`,
      );
      return res.data;
    },
  });
}

export function useUpsertPriceEntry(itemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      priceListId: string;
      unitPriceMinor: string;
      costMinor?: string;
      // RFL-202: string sets the value, null clears it, omitted leaves it.
      wholesalePriceMinor?: string | null;
      minPriceMinor?: string | null;
      maxPriceMinor?: string | null;
      discountPriceMinor?: string | null;
      vipPriceMinor?: string | null;
      // RL-994: the 3-decimal fuel price; null or omitted clears it.
      unitPriceMilli?: string | null;
    }) => {
      const res = await apiClient.post(`/pricing/price-lists/${input.priceListId}/entries`, {
        itemId,
        unitPriceMinor: input.unitPriceMinor,
        costMinor: input.costMinor,
        wholesalePriceMinor: input.wholesalePriceMinor,
        minPriceMinor: input.minPriceMinor,
        maxPriceMinor: input.maxPriceMinor,
        discountPriceMinor: input.discountPriceMinor,
        vipPriceMinor: input.vipPriceMinor,
        unitPriceMilli: input.unitPriceMilli,
      });
      return res.data as { id: string };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EFFECTIVE_KEY(itemId) });
    },
  });
}

/** Format minor units (string of bigint) to a major-unit string with `decimals` places. */
export function formatMinor(minor: string, decimals = 2): string {
  if (!/^-?\d+$/.test(minor)) return minor;
  const negative = minor.startsWith('-');
  const abs = negative ? minor.slice(1) : minor;
  const padded = abs.padStart(decimals + 1, '0');
  const cut = padded.length - decimals;
  const whole = padded.slice(0, cut);
  const frac = decimals > 0 ? `.${padded.slice(cut)}` : '';
  return `${negative ? '-' : ''}${whole}${frac}`;
}

/** Parse a major-unit string ("12.34") to minor units. Throws on bad input. */
export function parseMajor(s: string, decimals = 2): string {
  const m = /^(-?)(\d+)(?:\.(\d+))?$/.exec(s.trim());
  if (m === null) throw new Error(`bad money "${s}"`);
  const [, sign, whole, frac = ''] = m;
  if (frac.length > decimals) throw new Error(`too many fractional digits in "${s}"`);
  const padded = frac.padEnd(decimals, '0');
  return `${sign}${BigInt(`${whole}${padded}`)}`;
}
